import React from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { connect } from "react-redux";
import { setChangedVehicles } from "../../store/actions/project";
import { setSolution } from "../../store/actions/solution";
import { setLoadingRoutesList } from "../../store/actions/loading";
import API from "../../utils/axios";
import {
  setDragSelection,
  setDraggingType,
  setManualDragSelection
} from "../../store/actions/common";
import { useSnackbar } from "notistack";
import {
  dragFromRouteToRoute,
  dragInsideRoute,
  dragFromUnassignedToRoute,
  unassignStops
} from "../../store/actions/dragAndDrop";
import { client } from "../map/DriverMarkers";

function DragContext({
  children,
  solution,
  projectId,
  setDraggingType,
  setSolution,
  dragFromRouteToRoute,
  dragInsideRoute,
  setLoadingRoutesList,
  dragFromUnassignedToRoute,
  unassignStops,
  dragSelection,
  setDragSelection,
  setManualDragSelection,
  setChangedVehicles
}) {
  const { enqueueSnackbar } = useSnackbar();

  // Get route index by route ID
  const getIndex = id => {
    id = id.split("route-")[1];

    return solution.routes.findIndex(el => el.id === id);
  };

  // Drag end callback
  const onDragEnd = result => {
    const { source, destination, draggableId } = result;
    setDraggingType("");
    // dropped outside the list or nothing changed
    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        result.source.index === result.destination.index)
    ) {
      return;
    }

    const newSolution = { ...solution };

    // Set loading
    if (
      (source.droppableId === destination.droppableId &&
        result.source.index !== result.destination.index) ||
      source.droppableId !== destination.droppableId
    ) {
      setLoadingRoutesList(true);
    }

    dragSelection.list =
      dragSelection.list.length === 0 ? [draggableId] : dragSelection.list;

    let isBreakInDraggingList = false;
    if (
      source.droppableId !== "drivers" &&
      source.droppableId !== "unassigned"
    ) {
      const route = newSolution.routes[getIndex(source.droppableId)];
      route.stops.forEach(task => {
        if (dragSelection.list.includes(task.id) && task.type === "break")
          isBreakInDraggingList = true;
      });
    }

    // Route -> <-
    if (
      source.droppableId === destination.droppableId &&
      source.droppableId.includes("route-") &&
      result.source.index !== result.destination.index
    ) {
      // Reorder ignoring "start" and "end"
      dragInsideRoute({
        sourceIndex: source.index,
        sourceId: source.droppableId.split("route-")[1],
        destIndex: destination.index,
        selection: dragSelection.list
      });
    }

    // Route -> Route
    else if (
      destination.droppableId !== "unassigned" &&
      source.droppableId !== "unassigned" &&
      source.droppableId.includes("route-") &&
      destination.droppableId.includes("route-")
    ) {
      const route = newSolution.routes[getIndex(destination.droppableId)];

      if (isBreakInDraggingList && dragSelection.list.length === 1) {
        enqueueSnackbar("Can't drag and drop break outside from the route");
        setLoadingRoutesList(false);
        return;
      } else if (route.status === "finished") {
        enqueueSnackbar("Can't drag and drop stop to the completed route");
        setLoadingRoutesList(false);
        return;
      }
      const destIndex = destination.index + 1;

      dragFromRouteToRoute({
        sourceId: source.droppableId.split("route-")[1],
        destinationId: destination.droppableId.split("route-")[1],
        destIndex: destIndex,
        selection: dragSelection.list,
        isBreakInDraggingList: isBreakInDraggingList
      });
    }

    // Unassigned -> Route
    else if (
      source.droppableId === "unassigned" &&
      destination.droppableId.includes("route-")
    ) {
      const destIndex = destination.index + 1;
      const route = newSolution.routes[getIndex(destination.droppableId)];

      if (route.status === "finished") {
        enqueueSnackbar("Can't drag and drop stop to the completed route");
        setLoadingRoutesList(false);
        return;
      }

      dragFromUnassignedToRoute({
        destinationId: destination.droppableId.split("route-")[1],
        destIndex: destIndex,
        selection: dragSelection.list
      });
    }

    // Route -> Unassigned
    else if (
      destination.droppableId === "unassigned" &&
      source.droppableId.includes("route-")
    ) {
      if (isBreakInDraggingList && dragSelection.list.length === 1) {
        setLoadingRoutesList(false);
        enqueueSnackbar("Can't drag and drop break outside from route");
        return;
      }

      unassignStops({
        sourceId: source.droppableId.split("route-")[1],
        selection: dragSelection.list,
        isBreakInDraggingList: isBreakInDraggingList
      });
    }

    // Driver -> Route
    else if (
      destination.droppableId.includes("panel-") &&
      source.droppableId === "drivers"
    ) {
      const routeId = destination.droppableId.split("panel-")[1];
      const routeGroup =
        solution.routeGroups[Object.keys(solution.routeGroups)[0]];
      let relatedRouteIds = [];
      routeGroup.forEach(routes => {
        const routesId = routes.map(route => route.id);
        if (routes.map(route => route.id).includes(routeId)) {
          relatedRouteIds = routesId;
        }
      });

      const newSolution = { ...solution };
      Promise.all(
        relatedRouteIds.map(theRouteId => {
          const routeIndex = solution.routes.findIndex(
            el => el.id === theRouteId
          );
          // Count the change
          const route = newSolution.routes[routeIndex];
          setChangedVehicles(route.vehicle_id);
          return API.put(
            `/v1/projects/${projectId}/routes/${theRouteId}/drivers`,
            {
              driver_id: result.draggableId
            }
          );
        })
      )
        .then(results => {
          results.forEach(res => {
            const routeIndex = solution.routes.findIndex(
              el => el.id === res.data.id
            );
            client &&
              client.send(
                JSON.stringify({
                  type: "driver_change",
                  route_id: routeId,
                  driver_id: result.draggableId
                })
              );
            newSolution.routes[routeIndex] = res.data;
          });
        })
        .catch(error => {
          enqueueSnackbar("Something went wrong");
          console.log(error);
        })
        .finally(() => {
          setSolution(newSolution);
          setLoadingRoutesList(false);
        });
    }

    // Else
    else {
      setLoadingRoutesList(false);
      setManualDragSelection("", []);
      return;
    }

    setManualDragSelection("", []);
    setSolution(newSolution);
  };

  // Drag start callback
  const onDragStart = item => {
    // Add stop to drag selection if not present
    if (
      dragSelection.list &&
      (dragSelection.list.length === 0 ||
        !dragSelection.list.includes(item.draggableId)) &&
      (item.source.droppableId.includes("route") ||
        item.source.droppableId === "unassigned")
    ) {
      setManualDragSelection(item.source.droppableId, []);
      /*
      if (dragSelection.listId !== item.source.droppableId) {
        setDragSelection(item.source.droppableId, item.draggableId);
      } else if (!dragSelection.list.includes(item.draggableId)) {
        setManualDragSelection(dragSelection.listId, [item.draggableId]);
      }*/
    }

    // Set dragging type
    if (item.source.droppableId === "drivers") {
      setDraggingType("drivers");
    } else {
      setDraggingType("stops");
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
      {children}
    </DragDropContext>
  );
}

function mapStateToProps(state) {
  return {
    solution: state.project.solution,
    projectId: state.project.id,
    unassigned: state.project.solution
      ? state.project.solution.unassigned.stops
      : [],
    routes: state.project.solution ? state.project.solution.routes : [],
    drivers: state.project.drivers || [],
    dragSelection: state.common.dragSelection,
    changedVehicles: state.project.changedVehicles
  };
}

export default connect(mapStateToProps, {
  setSolution,
  setLoadingRoutesList,
  setDraggingType,
  setDragSelection,
  setManualDragSelection,
  dragFromRouteToRoute,
  dragInsideRoute,
  dragFromUnassignedToRoute,
  unassignStops,
  setChangedVehicles
})(DragContext);
